import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import SecureViewer from "./SecureViewer";
import NDAModal from "./NDAModal";
import ConfidentialityGateDialog, { useConfidentialityGate } from "@/components/compliance/ConfidentialityGate";
import { Folder, FolderOpen, FilePdf, FileXls, FileVideo, FileText, LockKey, Eye, ShieldCheck } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/lib/auth";

const FOLDER_LABELS = {
  pitch_deck: "Pitch Deck",
  financial_model: "Financial Model",
  financial_statements: "Financial Statements",
  cap_table: "Cap Table",
  legal: "Legal Documents",
  due_diligence: "Due Diligence",
  product_demo: "Product Demo",
  other: "Other Documents",
};

const FOLDER_ORDER = ["pitch_deck", "financial_model", "financial_statements", "cap_table", "legal", "due_diligence", "product_demo", "other"];

const docIcon = (name) => {
  const n = (name || "").toLowerCase();
  if (n.endsWith(".xls") || n.endsWith(".xlsx") || n.endsWith(".csv")) return <FileXls size={18} weight="fill" className="text-emerald-600" />;
  if (n.endsWith(".mp4") || n.endsWith(".mov") || n.endsWith(".webm")) return <FileVideo size={18} weight="fill" className="text-blue-600" />;
  return <FilePdf size={18} weight="fill" className="text-red-600 dark:text-red-400" />;
};

export default function DataRoom({ companyId, companyName }) {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState("pitch_deck");
  const [viewingDoc, setViewingDoc] = useState(null);
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [showNda, setShowNda] = useState(false);
  const [ndaLoading, setNdaLoading] = useState(true);
  const [pendingDoc, setPendingDoc] = useState(null);

  useEffect(() => {
    setLoading(true);
    setNdaLoading(true);
    Promise.all([
      api.get(`/companies/${companyId}/documents`),
      api.get(`/companies/${companyId}/nda/status`),
    ]).then(([docsRes, ndaRes]) => {
      setDocs(docsRes.data);
      setNdaAccepted(!!ndaRes.data.accepted);
    }).finally(() => {
      setLoading(false);
      setNdaLoading(false);
    });
  }, [companyId]);

  const byFolder = useMemo(() => {
    const map = {};
    FOLDER_ORDER.forEach((f) => (map[f] = []));
    docs.forEach((d) => { (map[d.folder] ||= []).push(d); });
    return map;
  }, [docs]);

  const activeDocs = byFolder[activeFolder] || [];
  const confidentiality = useConfidentialityGate();

  const openDoc = async (d) => {
    if (!ndaAccepted) {
      setPendingDoc(d);
      setShowNda(true);
      return;
    }
    // FCA Confidentiality gate — session-scoped
    const ok = await confidentiality.requestAccess();
    if (!ok) return;
    setViewingDoc(d);
  };

  const onNdaAccepted = async () => {
    setNdaAccepted(true);
    setShowNda(false);
    if (pendingDoc) {
      const ok = await confidentiality.requestAccess();
      if (ok) setViewingDoc(pendingDoc);
      setPendingDoc(null);
    }
  };

  if (ndaLoading) {
    return <div className="rounded-2xl border border-border bg-card p-12 text-center text-muted-foreground">Loading data room…</div>;
  }

  // NDA gate — show a full-width prompt instead of the data room until accepted
  if (!ndaAccepted) {
    return (
      <>
        <div className="rounded-2xl border border-border bg-card p-10 md:p-16 text-center overflow-hidden relative" data-testid="dataroom-nda-gate">
          <div className="absolute inset-0 grid-backdrop opacity-40 pointer-events-none" />
          <div className="relative">
            <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 grid place-items-center mb-5">
              <ShieldCheck size={32} weight="fill" className="text-emerald-700 dark:text-emerald-400" />
            </div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-400 font-bold mb-3">Confidential Data Room</div>
            <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight max-w-2xl mx-auto">
              Accept the NDA to unlock the {companyName} data room
            </h3>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
              All materials are watermarked with your identity and every view is logged for compliance. Downloads, screenshots, and copying are prohibited under the terms of the NDA.
            </p>
            <Button
              onClick={() => setShowNda(true)}
              data-testid="dataroom-open-nda-btn"
              className="mt-8 h-11 px-6 bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
            >
              <LockKey size={16} weight="fill" className="mr-2" /> Review NDA
            </Button>
          </div>
        </div>
        <NDAModal
          open={showNda}
          onClose={() => setShowNda(false)}
          onAccept={onNdaAccepted}
          companyName={companyName}
          companyId={companyId}
        />
      </>
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <ShieldCheck size={13} weight="fill" className="text-emerald-600" />
          NDA accepted by <b className="text-foreground">{user?.name}</b>
        </div>
        <div className="num-display">{docs.length} document{docs.length === 1 ? "" : "s"}</div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4 rounded-2xl border border-border bg-card overflow-hidden">
        <div className="border-b lg:border-b-0 lg:border-r border-border p-3">
          <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground px-2 pb-2 flex items-center gap-1.5">
            <LockKey size={11} weight="fill" /> Data Room
          </div>
          <ul className="space-y-0.5">
            {FOLDER_ORDER.map((f) => {
              const count = byFolder[f]?.length || 0;
              const active = activeFolder === f;
              return (
                <li key={f}>
                  <button
                    onClick={() => setActiveFolder(f)}
                    data-testid={`dataroom-folder-${f}`}
                    className={`w-full text-left px-2 py-1.5 rounded-md text-sm flex items-center gap-2 transition-colors ${
                      active ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                    }`}
                  >
                    {active ? <FolderOpen size={15} weight="fill" className="text-emerald-600" /> : <Folder size={15} />}
                    <span className="flex-1 truncate">{FOLDER_LABELS[f]}</span>
                    <span className="text-[10px] tabular-nums text-muted-foreground">{count}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-display font-bold text-lg">{FOLDER_LABELS[activeFolder]}</h3>
            <span className="text-xs text-muted-foreground">{activeDocs.length} document{activeDocs.length === 1 ? "" : "s"}</span>
          </div>
          {loading ? (
            <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-12 rounded bg-secondary animate-pulse" />)}</div>
          ) : activeDocs.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No documents in this folder yet.</div>
          ) : (
            <ul className="space-y-1.5">
              {activeDocs.map((d) => (
                <li key={d.id}>
                  <button
                    onClick={() => openDoc(d)}
                    data-testid={`dataroom-doc-${d.id}`}
                    className="w-full flex items-center gap-3 p-3 rounded-md border border-border hover:border-emerald-500 hover:bg-secondary/40 transition-all group text-left"
                  >
                    <div className="h-9 w-9 rounded bg-secondary grid place-items-center shrink-0">{docIcon(d.name)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{d.name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {d.size ? `${(d.size / 1024).toFixed(0)} KB · ` : ""}Uploaded {formatDate(d.uploaded_at)} · v{d.version}
                      </div>
                    </div>
                    <div className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-emerald-600 dark:text-emerald-400 flex items-center gap-1 text-xs font-medium">
                      <Eye size={14} weight="bold" /> View
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <SecureViewer document={viewingDoc} open={!!viewingDoc} onClose={() => setViewingDoc(null)} />
      <ConfidentialityGateDialog
        needed={confidentiality.needed}
        disclaimer={confidentiality.disclaimer}
        onAccept={confidentiality.accept}
        onCancel={confidentiality.cancel}
      />
    </div>
  );
}
